import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { useToast } from '@/components/ui'
import { clearSessionLockState } from '@/lock/store'

import { authApi, authKeys, type AuthState } from './auth'
import { ApiError, errorMessage } from './client'
import { captureIdentity, setActiveIdentity } from './identity'

/**
 * Выход: этот браузер (`all = false`) или все устройства. Кэш данных очищается.
 * «Выйти везде» с ошибкой запроса остаётся на экране: сессии на других устройствах живы.
 */
export function useLogout(all = false) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const toast = useToast()
  return useMutation({
    onMutate: () => captureIdentity(),
    mutationFn: () => (all ? authApi.logoutAll() : authApi.logout()),
    onSettled: async (_data, error, _variables, identity) => {
      if (!identity?.isUnchanged()) return
      if (all && error) {
        const message =
          error instanceof ApiError && error.isNetwork
            ? 'Нет связи — сессии не закрыты, попробуй ещё раз'
            : errorMessage(error, 'Не удалось выйти на всех устройствах')
        toast.show({ message, tone: 'danger' })
        return
      }
      clearSessionLockState()
      const clearing = setActiveIdentity(null)
      const clearedIdentity = captureIdentity()
      await clearing
      if (!clearedIdentity.isUnchanged()) return
      queryClient.clear()
      queryClient.setQueryData<AuthState>(authKeys.state, {
        setup_required: false,
        authenticated: false,
      })
      navigate('/login', { replace: true })
    },
  })
}
